<template>
  <box-view class="printed-bills-view" title="Lịch sử in Bill" :showBack="true" @close="$router.push('/admin')">
    <div class="container-fluid py-3">
      
      <!-- Filters -->
      <div class="card shadow-sm mb-4">
        <div class="card-body">
          <div class="row g-3">
            <div class="col-md-4">
              <label class="small font-weight-bold">Tìm kiếm</label>
              <input type="text" class="form-control" v-model="filters.search" placeholder="Mã bill, mã đơn, khách hàng...">
            </div>
            <div class="col-md-3">
              <label class="small font-weight-bold">Từ ngày</label>
              <input type="date" class="form-control" v-model="filters.startDate">
            </div>
            <div class="col-md-3">
              <label class="small font-weight-bold">Đến ngày</label>
              <input type="date" class="form-control" v-model="filters.endDate">
            </div>
            <div class="col-md-2 d-flex align-items-end">
              <button class="btn btn-outline-secondary btn-block" @click="resetFilters">
                <i class="fas fa-sync"></i> Reset
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="text-center py-5">
        <i class="fas fa-spinner fa-spin fa-2x text-primary"></i>
        <p class="mt-2 text-muted">Đang tải dữ liệu...</p>
      </div>

      <!-- History Table -->
      <div v-else class="card shadow-sm">
        <div class="card-header bg-light d-flex justify-content-between align-items-center py-2">
          <span class="small font-weight-bold"><i class="fas fa-receipt mr-1"></i> Tổng: {{ serverTotal }} bill</span>
          <span class="small text-muted">Trang {{ currentPage }} / {{ totalPages }}</span>
        </div>
        <div class="table-responsive">
          <table class="table table-hover mb-0">
            <thead class="bg-light">
              <tr>
                <th>Mã Bill</th>
                <th>Mã Đơn</th>
                <th>Khách hàng</th>
                <th class="text-right">Tổng tiền</th>
                <th>Thu ngân</th>
                <th>Thời gian in</th>
                <th class="text-center">Thao tác</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="bill in paginatedBills" :key="bill.id">
                <td class="font-weight-bold">{{ bill.billNo }}</td>
                <td>{{ bill.orderId }}</td>
                <td>
                  {{ getCustomerName(bill.customerId) }}
                  <div class="small text-muted">{{ bill.printType === 'receipt' ? 'Biên nhận' : 'Hóa đơn' }}</div>
                </td>
                <td class="text-right font-weight-bold">{{ Math.floor(Number(bill.totalAmount || 0)).toLocaleString('vi-VN') }}₫</td>
                <td>{{ getUserName(bill.printedByUserId) }}</td>
                <td>{{ formatDateTime(bill.printedAt) }}</td>
                <td class="text-center">
                  <button class="btn btn-sm btn-info mr-1" @click="viewBill(bill)" title="Xem lại">
                    <i class="fas fa-eye"></i>
                  </button>
                  <button class="btn btn-sm btn-primary" @click="reprintBill(bill)" title="In lại">
                    <i class="fas fa-print"></i>
                  </button>
                </td>
              </tr>
              <tr v-if="paginatedBills.length === 0 && !loading">
                <td colspan="7" class="text-center py-5 text-muted">
                  <i class="fas fa-receipt fa-3x mb-3 opacity-25"></i>
                  <p>Không tìm thấy lịch sử in ấn nào</p>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Pagination -->
      <nav v-if="totalPages > 1" class="mt-4">
        <ul class="pagination pagination-sm justify-content-center flex-wrap">
          <li class="page-item" :class="{ disabled: currentPage === 1 }">
            <a class="page-link" href="#" @click.prevent="goToPage(1)" title="Trang đầu">««</a>
          </li>
          <li class="page-item" :class="{ disabled: currentPage === 1 }">
            <a class="page-link" href="#" @click.prevent="goToPage(currentPage - 1)">«</a>
          </li>
          <li v-for="(page, idx) in visiblePages" :key="'p'+idx"
              class="page-item"
              :class="{ active: page === currentPage, disabled: page === '...' }">
            <a class="page-link" href="#"
               @click.prevent="page !== '...' && goToPage(page)">{{ page }}</a>
          </li>
          <li class="page-item" :class="{ disabled: currentPage === totalPages }">
            <a class="page-link" href="#" @click.prevent="goToPage(currentPage + 1)">»</a>
          </li>
          <li class="page-item" :class="{ disabled: currentPage === totalPages }">
            <a class="page-link" href="#" @click.prevent="goToPage(totalPages)" title="Trang cuối">»»</a>
          </li>
        </ul>
        <div class="text-center text-muted small">Trang {{ currentPage }} / {{ totalPages }} ({{ serverTotal }} kết quả)</div>
      </nav>
    </div>

    <!-- Bill Detail Modal -->
    <div v-if="selectedBill" class="bill-modal-overlay d-flex align-items-center justify-content-center" @click.self="selectedBill = null">
       <div class="bill-modal-content card shadow-lg border-0 overflow-hidden">
         <div class="card-header bg-dark text-white d-flex justify-content-between align-items-center">
            <h6 class="mb-0">CHI TIẾT BILL #{{ selectedBill.billNo }} (SNAPSHOT)</h6>
            <button class="btn btn-sm btn-link text-white p-0" @click="selectedBill = null">✕</button>
         </div>
         <div class="card-body bg-light p-4 overflow-auto" style="max-height: 80vh;">
            <div class="d-flex justify-content-center">
              <bill-template :order="selectedBill.snapshot.order" :settings="selectedBill.snapshot.settings" />
            </div>
         </div>
         <div class="card-footer bg-white text-right">
            <button class="btn btn-primary" @click="reprintBill(selectedBill)">
               <i class="fas fa-print mr-2"></i> In lại (Reprint)
            </button>
         </div>
       </div>
    </div>
  </box-view>
</template>

<script>
import BillTemplate from '@/components/reports/BillTemplate.vue';

export default {
  name: 'PrintedBills',
  components: {
    BillTemplate
  },
  props: {
    printedBills: {
      type: Array,
      default: () => []
    },
    customers: {
      type: Array,
      default: () => []
    },
    users: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      filters: {
        search: '',
        startDate: '',
        endDate: ''
      },
      selectedBill: null,
      currentPage: 1,
      pageSize: 50,
      // Server-side pagination data
      serverBills: [],
      serverTotal: 0,
      serverPaginationSupported: false,
      loading: false
    };
  },
  watch: {
    filters: {
      deep: true,
      handler() {
        this.currentPage = 1;
        // Debounce: wait 400ms after last keystroke before fetching
        clearTimeout(this._searchTimer);
        this._searchTimer = setTimeout(() => {
          this.fetchPage();
        }, 400);
      }
    }
  },
  computed: {
    // Use serverBills if we fetched data ourselves, otherwise fallback to prop
    allBills() {
      return this.serverBills.length > 0 ? this.serverBills : this.printedBills;
    },
    filteredBills() {
      if (this.serverPaginationSupported) {
        // Server already filtered and paginated, return as-is
        return this.allBills;
      }
      // Client-side fallback filtering
      return this.allBills.filter(bill => {
        const search = this.filters.search.toLowerCase();
        const matchSearch = !search || 
          bill.billNo.toLowerCase().includes(search) || 
          bill.orderId.toLowerCase().includes(search) || 
          this.getCustomerName(bill.customerId).toLowerCase().includes(search);
        
        const billDate = (bill.printedAt || '').split('T')[0].split(' ')[0];
        const matchStart = !this.filters.startDate || billDate >= this.filters.startDate;
        const matchEnd = !this.filters.endDate || billDate <= this.filters.endDate;

        return matchSearch && matchStart && matchEnd;
      });
    },
    totalPages() {
      if (this.serverPaginationSupported) {
        // Server told us the total, use it
        return Math.max(1, Math.ceil(this.serverTotal / this.pageSize));
      }
      // Fallback: client-side pagination
      return Math.max(1, Math.ceil(this.filteredBills.length / this.pageSize));
    },
    paginatedBills() {
      if (this.serverPaginationSupported) {
        // Data is already paginated from server, just return filteredBills (with client search applied)
        return this.filteredBills;
      }
      // Client-side pagination
      const start = (this.currentPage - 1) * this.pageSize;
      return this.filteredBills.slice(start, start + this.pageSize);
    },
    visiblePages() {
      const total = this.totalPages;
      const current = this.currentPage;
      const delta = 2;
      const pages = [];
      if (total <= 7) {
        for (let i = 1; i <= total; i++) pages.push(i);
        return pages;
      }
      pages.push(1);
      const rangeStart = Math.max(2, current - delta);
      const rangeEnd = Math.min(total - 1, current + delta);
      if (rangeStart > 2) pages.push('...');
      for (let i = rangeStart; i <= rangeEnd; i++) pages.push(i);
      if (rangeEnd < total - 1) pages.push('...');
      pages.push(total);
      return pages;
    }
  },
  mounted() {
    this.fetchPage();
  },
  methods: {
    fetchPage() {
      this.loading = true;
      const params = { 
        page: this.currentPage, 
        per_page: this.pageSize 
      };
      // Send search/date filters to server
      if (this.filters.search) params.search = this.filters.search;
      if (this.filters.startDate) params.start_date = this.filters.startDate;
      if (this.filters.endDate) params.end_date = this.filters.endDate;
      
      this.$root.api.get('logach/getLoGachPrintedBills', { params }).then(res => {
        if (res.data.status === 'OK') {
          // Try reading from root level first (new format: success('OK', {list, total}))
          // Then fallback to message level (old format: success({list}))
          const list = res.data.list || (res.data.message && typeof res.data.message === 'object' ? res.data.message.list : null) || [];
          const total = res.data.total || (res.data.message && typeof res.data.message === 'object' ? res.data.message.total : null) || 0;
          
          this.serverBills = list;
          
          // Detect if server supports pagination
          // If total > list.length, server is doing real pagination
          // If total === list.length and total > 0, could be either (check per_page)
          if (total > 0 && total !== list.length) {
            // Server returned a different total than list count = real server pagination
            this.serverPaginationSupported = true;
            this.serverTotal = total;
          } else if (res.data.total !== undefined || (res.data.message && typeof res.data.message === 'object' && res.data.message.total !== undefined)) {
            // Server explicitly returned 'total' field = new backend
            this.serverPaginationSupported = true;
            this.serverTotal = total;
          } else {
            // Old backend - no total field, received all data (up to old limit)
            // Fall back to client-side pagination
            this.serverPaginationSupported = false;
            this.serverTotal = list.length;
          }

          console.log('[PrintedBills] API response:', { 
            listCount: list.length, 
            total, 
            serverPagination: this.serverPaginationSupported,
            rawTotal: res.data.total,
            rawMsgTotal: res.data.message?.total
          });
        }
      }).catch(err => {
        console.error('Error fetching printed bills:', err);
        // Fallback to prop data
        this.serverPaginationSupported = false;
        this.serverTotal = this.printedBills.length;
      }).finally(() => {
        this.loading = false;
      });
    },
    goToPage(page) {
      if (page < 1) page = 1;
      if (page > this.totalPages) page = this.totalPages;
      if (page === this.currentPage) return;
      this.currentPage = page;
      if (this.serverPaginationSupported) {
        // Fetch new page from server
        this.fetchPage();
      }
      // If client-side pagination, paginatedBills computed will handle it
    },
    getCustomerName(id) {
      const c = this.customers.find(x => x.id === id);
      return c ? c.name : 'Khách lẻ';
    },
    getUserName(id) {
      const u = this.users.find(x => x.id === id);
      return u ? u.name : 'Admin';
    },
    formatDateTime(iso) {
      return new Date(iso).toLocaleString('vi-VN');
    },
    resetFilters() {
      this.filters = { search: '', startDate: '', endDate: '' };
      this.currentPage = 1;
      if (this.serverPaginationSupported) {
        this.fetchPage();
      }
    },
    viewBill(bill) {
      this.selectedBill = bill;
    },
    reprintBill(bill) {
      this.$emit('reprint-bill', bill.id);
      this.selectedBill = null;
    }
  }
};
</script>

<style scoped>
.bill-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  z-index: 9999;
}

.bill-modal-content {
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  animation: modal-fade-in 0.3s ease-out;
}

@keyframes modal-fade-in {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}

.card-body::-webkit-scrollbar {
  width: 6px;
}
.card-body::-webkit-scrollbar-thumb {
  background-color: #ccc;
  border-radius: 3px;
}
</style>
